import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Plus, 
  Play, 
  Pause, 
  Trash2, 
  Copy, 
  Zap,
  TrendingUp,
  Volume2,
  Clock,
  AlertTriangle,
  CheckCircle,
  Fish,
  Brain,
  Target,
  X
} from 'lucide-react'
import { strategiesApi } from '../lib/api'
import { cn } from '../lib/utils'
import { StrategyBuilderModal } from '../components/StrategyBuilderModal'

interface Strategy {
  id: string
  name: string
  description?: string
  rules: StrategyRule[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface StrategyRule {
  condition: string
  value: string
  action: string
  positionSize?: number
}

const conditionIcons: Record<string, React.ElementType> = {
  PRICE_ABOVE: TrendingUp,
  PRICE_BELOW: TrendingUp,
  PRICE_CHANGE_PCT_ABOVE: TrendingUp,
  PRICE_CHANGE_PCT_BELOW: TrendingUp,
  VOLUME_ABOVE: Volume2,
  LIQUIDITY_ABOVE: Volume2,
  SPREAD_BELOW: Target,
  WHALE_BUYING: Fish,
  WHALE_SELLING: Fish,
  AI_CONFIDENCE_ABOVE: Brain,
  TIME_REMAINING_BELOW: Clock,
  MARKET_CATEGORY_IS: Target,
  MARKET_STATUS_IS: CheckCircle,
}

const conditionLabels: Record<string, string> = {
  PRICE_ABOVE: 'Price Above',
  PRICE_BELOW: 'Price Below',
  PRICE_CHANGE_PCT_ABOVE: 'Price Change % Above',
  PRICE_CHANGE_PCT_BELOW: 'Price Change % Below',
  VOLUME_ABOVE: 'Volume Above',
  LIQUIDITY_ABOVE: 'Liquidity Above',
  SPREAD_BELOW: 'Spread Below',
  WHALE_BUYING: 'Whale Buying',
  WHALE_SELLING: 'Whale Selling',
  AI_CONFIDENCE_ABOVE: 'AI Confidence Above',
  TIME_REMAINING_BELOW: 'Time Remaining Below',
  MARKET_CATEGORY_IS: 'Market Category Is',
  MARKET_STATUS_IS: 'Market Status Is',
}

const actionLabels: Record<string, string> = {
  BUY_YES: 'Buy YES',
  BUY_NO: 'Buy NO',
  SELL_YES: 'Sell YES',
  SELL_NO: 'Sell NO',
  ALERT: 'Send Alert',
}

export default function Strategies() {
  const queryClient = useQueryClient()
  const [showBuilder, setShowBuilder] = useState(false)
  const [editingStrategy, setEditingStrategy] = useState<Strategy | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['strategies'],
    queryFn: () => strategiesApi.getAll().then(r => r.data),
  })

  const toggleMutation = useMutation({
    mutationFn: (id: string) => strategiesApi.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategies'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => strategiesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategies'] })
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: (strategy: Strategy) => 
      strategiesApi.create({
        name: `${strategy.name} (Copy)`,
        description: strategy.description,
        rules: strategy.rules,
        isActive: false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategies'] })
    },
  })

  const strategies: Strategy[] = data?.strategies || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Strategy Builder</h1>
          <p className="text-gray-600">
            Create automated trading rules with visual conditions
          </p>
        </div>
        <button
          onClick={() => {
            setEditingStrategy(null)
            setShowBuilder(true)
          }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Strategy
        </button>
      </div>

      {/* Templates Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-center space-x-2 mb-4">
          <Zap className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900">Quick Start Templates</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { id: 'momentum', name: 'Momentum Breakout', icon: TrendingUp, desc: 'Buy on price breakout with volume' },
            { id: 'whale', name: 'Whale Follower', icon: Fish, desc: 'Copy whale wallet trades' },
            { id: 'ai', name: 'AI Confidence', icon: Brain, desc: 'Trade high AI confidence signals' },
            { id: 'liquidity', name: 'Liquid Scalper', icon: Volume2, desc: 'Trade liquid markets only' },
          ].map((template) => {
            const Icon = template.icon
            return (
              <button
                key={template.id}
                onClick={() => {
                  strategiesApi.getTemplates().then(res => {
                    const t = res.data.templates.find((x: any) => x.id.includes(template.id))
                    if (t) {
                      setEditingStrategy({
                        id: 'new',
                        name: t.name,
                        description: t.description,
                        rules: t.rules,
                        isActive: false,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                      } as Strategy)
                      setShowBuilder(true)
                    }
                  })
                }}
                className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all text-left"
              >
                <Icon className="w-8 h-8 text-blue-600 mb-3" />
                <p className="font-medium text-gray-900">{template.name}</p>
                <p className="text-xs text-gray-500 mt-1">{template.desc}</p>
              </button>
            )
          })}
        </div>
      </div>

      {/* Strategies List */}
      {strategies.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Zap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No strategies yet</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            Create your first automated trading strategy with visual rule builder. 
            No coding required!
          </p>
          <button
            onClick={() => {
              setEditingStrategy(null)
              setShowBuilder(true)
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Your First Strategy
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {strategies.map((strategy) => (
            <div
              key={strategy.id}
              className={cn(
                'bg-white rounded-xl border p-6 transition-all',
                strategy.isActive 
                  ? 'border-blue-200 shadow-sm' 
                  : 'border-gray-200'
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h3 className="font-semibold text-gray-900">{strategy.name}</h3>
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        strategy.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {strategy.isActive ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  
                  {strategy.description && (
                    <p className="text-sm text-gray-600 mt-1">{strategy.description}</p>
                  )}

                  {/* Rules Preview */}
                  <div className="mt-4 space-y-2">
                    {strategy.rules.slice(0, 3).map((rule, idx) => {
                      const Icon = conditionIcons[rule.condition] || Target
                      return (
                        <div key={idx} className="flex items-center text-sm">
                          <Icon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-600">
                            {conditionLabels[rule.condition]} <strong>{rule.value}</strong>
                          </span>
                          <span className="mx-2 text-gray-300">→</span>
                          <span className={cn(
                            'font-medium',
                            rule.action.includes('BUY') ? 'text-green-600' : 
                            rule.action.includes('SELL') ? 'text-red-600' : 'text-blue-600'
                          )}>
                            {actionLabels[rule.action]}
                          </span>
                          {rule.positionSize && (
                            <span className="text-gray-400 ml-2">(${rule.positionSize})</span>
                          )}
                        </div>
                      )
                    })}
                    {strategy.rules.length > 3 && (
                      <p className="text-sm text-gray-400">
                        +{strategy.rules.length - 3} more rules
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => toggleMutation.mutate(strategy.id)}
                    disabled={toggleMutation.isPending}
                    className={cn(
                      'p-2 rounded-lg transition-colors',
                      strategy.isActive
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    )}
                    title={strategy.isActive ? 'Pause' : 'Activate'}
                  >
                    {strategy.isActive ? (
                      <Pause className="w-5 h-5" />
                    ) : (
                      <Play className="w-5 h-5" />
                    )}
                  </button>

                  <button
                    onClick={() => duplicateMutation.mutate(strategy)}
                    disabled={duplicateMutation.isPending}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="w-5 h-5" />
                  </button>

                  <button
                    onClick={() => {
                      setEditingStrategy(strategy)
                      setShowBuilder(true)
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => {
                      if (confirm('Delete this strategy?')) {
                        deleteMutation.mutate(strategy.id)
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Builder Modal */}
      {showBuilder && (
        <StrategyBuilderModal
          strategy={editingStrategy}
          onClose={() => {
            setShowBuilder(false)
            setEditingStrategy(null)
          }}
        />
      )}
    </div>
  )
}
